import { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { createPathComponent } from '@react-leaflet/core';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { Link } from 'react-router-dom';
import { useMapMarkers } from '../../../api/map';
import { useAuth } from '../../../hooks/useAuth';
import { UserRole } from '@spi/shared';
import type { MapMarker } from '@spi/shared';
import { PageMeta } from '../shared/PageMeta';
import { OrgIcon } from '../shared/EntityIcons';

// React wrapper for Leaflet MarkerClusterGroup
const MarkerClusterGroup = createPathComponent(
  ({ children: _c, ...props }: any, ctx: any) => {
    const clusterGroup = L.markerClusterGroup(props);
    return {
      instance: clusterGroup,
      context: { ...ctx, layerContainer: clusterGroup },
    };
  },
  (instance: any, props: any, prevProps: any) => {
    if (props.iconCreateFunction !== prevProps.iconCreateFunction) {
      (instance as any).options.iconCreateFunction = props.iconCreateFunction;
    }
  },
);

// Compute a recent NDVI 8-day composite date (~16 days behind to ensure availability)
function getNdviDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 16);
  const year = d.getFullYear();
  const startOfYear = new Date(year, 0, 1);
  const dayOfYear = Math.floor((d.getTime() - startOfYear.getTime()) / 86400000) + 1;
  const periodStart = Math.floor((dayOfYear - 1) / 8) * 8 + 1;
  const result = new Date(year, 0, periodStart);
  return result.toISOString().split('T')[0];
}

// Custom marker icons
const recipientIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#059669"/>
      <circle cx="12" cy="12" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
  popupAnchor: [0, -36],
});

const donorIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#7c3aed"/>
      <circle cx="12" cy="12" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
  popupAnchor: [0, -36],
});

function createClusterIconFactory(color: string) {
  return (cluster: L.MarkerCluster) => {
    const count = cluster.getChildCount();
    const size = count < 10 ? 36 : count < 100 ? 42 : 48;
    return L.divIcon({
      html: `<div style="
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${color};
        color: white;
        border-radius: 50%;
        font-size: ${count < 100 ? 14 : 12}px;
        font-weight: 600;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        border: 2px solid white;
      ">${count}</div>`,
      className: '',
      iconSize: L.point(size, size),
    });
  };
}

const recipientClusterIcon = createClusterIconFactory('#059669');
const donorClusterIcon = createClusterIconFactory('#7c3aed');

type FilterRole = 'all' | 'recipient' | 'donor';

export function MapPage() {
  const { user } = useAuth();
  const { data: markers, isLoading } = useMapMarkers();
  const [roleFilter, setRoleFilter] = useState<FilterRole>('all');
  const [showNdvi, setShowNdvi] = useState(false);
  const ndviDate = useMemo(() => getNdviDate(), []);
  const isAdmin = user?.role === UserRole.ADMIN;

  const filteredMarkers = useMemo(() => {
    if (!markers) return [];
    if (roleFilter === 'all') return markers;
    return markers.filter((m: MapMarker) => m.role === roleFilter);
  }, [markers, roleFilter]);

  const recipientMarkers = useMemo(() => filteredMarkers.filter((m) => m.role === UserRole.RECIPIENT), [filteredMarkers]);
  const donorMarkers = useMemo(() => filteredMarkers.filter((m) => m.role === UserRole.DONOR), [filteredMarkers]);

  const recipientCount = markers?.filter((m: MapMarker) => m.role === UserRole.RECIPIENT).length ?? 0;
  const donorCount = markers?.filter((m: MapMarker) => m.role === UserRole.DONOR).length ?? 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <PageMeta title="Map" description="View organization locations on the map" path="/marketplace/map" />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl flex items-center gap-2"><OrgIcon className="w-7 h-7 text-spi-green" />Organization Map</h2>
          <p className="text-spi-slate text-sm mt-1">
            {isAdmin ? 'Showing recipients and donors worldwide' : 'Showing recipient organizations worldwide'}
          </p>
        </div>

      </div>

      {/* Filter chips & NDVI toggle */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => setRoleFilter(roleFilter === 'recipient' ? 'all' : 'recipient')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer select-none ${
            roleFilter === 'recipient'
              ? 'bg-emerald-600 text-white'
              : 'bg-spi-parchment text-spi-slate hover:bg-spi-seed'
          }`}
        >
          <span className={`w-2.5 h-2.5 rounded-full inline-block ${roleFilter === 'recipient' ? 'bg-white' : 'bg-emerald-600'}`} />
          Recipients ({recipientCount})
        </button>
        {isAdmin && (
          <button
            onClick={() => setRoleFilter(roleFilter === 'donor' ? 'all' : 'donor')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer select-none ${
              roleFilter === 'donor'
                ? 'bg-violet-600 text-white'
                : 'bg-spi-parchment text-spi-slate hover:bg-spi-seed'
            }`}
          >
            <span className={`w-2.5 h-2.5 rounded-full inline-block ${roleFilter === 'donor' ? 'bg-white' : 'bg-violet-600'}`} />
            Donors ({donorCount})
          </button>
        )}
        <label className="flex items-center gap-1.5 text-sm text-spi-slate cursor-pointer select-none ml-auto">
          <input
            type="checkbox"
            checked={showNdvi}
            onChange={(e) => setShowNdvi(e.target.checked)}
            className="rounded border-gray-300 text-spi-green focus:ring-spi-green"
          />
          Vegetation Index (NDVI)
        </label>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[500px] card">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
        </div>
      ) : (
        <div className="card overflow-hidden" style={{ height: '600px' }}>
          <MapContainer
            center={[0, 25]}
            zoom={3}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
            />
            {showNdvi && (
              <TileLayer
                url={`https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_NDVI_8Day/default/${ndviDate}/GoogleMapsCompatible_Level9/{z}/{y}/{x}.png`}
                attribution='NDVI: <a href="https://earthdata.nasa.gov">NASA GIBS</a>'
                opacity={0.55}
                maxNativeZoom={9}
              />
            )}
            <MarkerClusterGroup
              maxClusterRadius={50}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              iconCreateFunction={recipientClusterIcon}
            >
              {recipientMarkers.map((marker: MapMarker) => (
                <Marker
                  key={marker.id}
                  position={[marker.latitude, marker.longitude]}
                  icon={recipientIcon}
                >
                  <Popup>
                    <div className="min-w-[180px]">
                      <Link to={`/marketplace/organizations/${marker.id}`} className="font-semibold text-sm text-spi-green hover:underline">{marker.organizationName}</Link>
                      <p className="text-xs text-gray-500 capitalize">{marker.role} &middot; {marker.country}</p>
                      <p className="text-xs text-gray-500 capitalize">{marker.orgType.replace('_', ' ')}</p>
                      {marker.activeProjects !== undefined && marker.activeProjects > 0 && (
                        <Link
                          to={`/marketplace/organizations/${marker.id}`}
                          className="text-xs text-emerald-600 font-medium mt-1 block hover:underline"
                        >
                          {marker.activeProjects} active project{marker.activeProjects > 1 ? 's' : ''} &rarr;
                        </Link>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
            {isAdmin && (
              <MarkerClusterGroup
                maxClusterRadius={50}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
                iconCreateFunction={donorClusterIcon}
              >
                {donorMarkers.map((marker: MapMarker) => (
                  <Marker
                    key={marker.id}
                    position={[marker.latitude, marker.longitude]}
                    icon={donorIcon}
                  >
                    <Popup>
                      <div className="min-w-[180px]">
                        <Link to={`/marketplace/organizations/${marker.id}`} className="font-semibold text-sm text-spi-green hover:underline">{marker.organizationName}</Link>
                        <p className="text-xs text-gray-500 capitalize">{marker.role} &middot; {marker.country}</p>
                        <p className="text-xs text-gray-500 capitalize">{marker.orgType.replace('_', ' ')}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}
          </MapContainer>
        </div>
      )}

      {!isLoading && filteredMarkers.length === 0 && (
        <div className="text-center py-12 text-spi-slate">
          <p className="text-lg font-semibold mb-1">No organizations with locations</p>
          <p className="text-sm">Organizations need to set their coordinates in their profile to appear on the map.</p>
        </div>
      )}
    </div>
  );
}
